import React, { useRef, useState, useEffect } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Panel } from "primereact/panel";
import { TabMenu } from "primereact/tabmenu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { MenuItem } from "primereact/menuitem";
import { apiService } from "../../../api/api";
import AssetIdInput from "./AssetIdInput";
import CustomAssetExportDialog from "./CustomAssetExportDialog";
import { trackToolAction } from "../../../utils/analytics";

interface ScrapeJob {
  id: string;
  title: string;
  brand: string;
  tableName?: string;
  assetCount: number;
  status: "queued" | "running" | "completed" | "failed";
  createdAt: string;
  createdBy?: string;
  completedAt?: string;
  excelDownloadUrl?: string;
  bigQueryTableName?: string[];
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

const isValidTableName = (tableName: string): boolean => {
  const pattern = /^[A-Z]{1,6}$/;
  return pattern.test(tableName.trim());
};

const MusicAssetScraper = () => {
  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [invalidIds, setInvalidIds] = useState<string[]>([]);
  const [title, setTitle] = useState<string>("");
  const [brand, setBrand] = useState<string>("");
  const [tableName, setTableName] = useState<string>("");
  const [pasteText, setPasteText] = useState<string>("");
  const [scrapeJobs, setScrapeJobs] = useState<ScrapeJob[]>([]);
  const [deletedScrapeJobs, setDeletedScrapeJobs] = useState<ScrapeJob[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingJobs, setLoadingJobs] = useState<boolean>(true);
  const [activeHistoryTab, setActiveHistoryTab] = useState<number>(0);
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [reconciling, setReconciling] = useState<boolean>(false);
  const [exportJob, setExportJob] = useState<ScrapeJob | null>(null);

  const toast = useRef<Toast>(null);

  // Load scrape jobs on component mount
  useEffect(() => {
    loadScrapeJobs();
    loadDeletionHistory();
  }, []);

  const loadScrapeJobs = async () => {
    try {
      setLoadingJobs(true);
      const jobs = (await apiService.scrapers.getScrapeJobs()) as ScrapeJob[];
      setScrapeJobs(jobs.filter((job) => !job.isDeleted));
    } catch (error) {
      console.error("Error loading scrape jobs:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load scrape jobs",
        life: 3000,
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadDeletionHistory = async () => {
    try {
      const deletedJobs =
        (await apiService.scrapers.getScrapeJobDeletionHistory()) as ScrapeJob[];
      setDeletedScrapeJobs(deletedJobs);
    } catch (error) {
      console.error("Error loading scrape deletion history:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load scrape deletion history",
        life: 3000,
      });
    }
  };

  const handleAssetIdsParsed = (validIds: string[], invalidIds: string[]) => {
    setAssetIds(validIds);
    setInvalidIds(invalidIds);

    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail: `Parsed ${validIds.length} valid asset IDs${
        invalidIds.length > 0 ? `, ${invalidIds.length} invalid IDs found` : ""
      }`,
      life: 3000,
    });
  };

  const handleStartScrape = async () => {
    if (!title || !brand || !tableName) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide title, brand, and table name",
        life: 3000,
      });
      return;
    }

    if (!isValidTableName(tableName)) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Table name must be 1-6 uppercase letters only",
        life: 3000,
      });
      return;
    }

    if (assetIds.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide asset IDs to scrape",
        life: 3000,
      });
      return;
    }

    trackToolAction("DataHealthTool", "scrape_start");
    try {
      setLoading(true);

      const response = await apiService.scrapers.startMusicAssetScrape({
        title,
        brand,
        tableName,
        assetIds,
      });

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: response.message,
        life: 3000,
      });

      // Reload scrape jobs to show the new one
      await loadScrapeJobs();

      // Clear form
      setAssetIds([]);
      setInvalidIds([]);
      setTitle("");
      setBrand("");
      setTableName("");
    } catch (error) {
      console.error("Error starting scrape:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.toString() || "Failed to start scrape",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRepull = async (job: ScrapeJob) => {
    trackToolAction("DataHealthTool", "repull");
    try {
      setLoading(true);
      const response = await apiService.scrapers.repullScrapeJob(job.id);
      toast.current?.show({
        severity: "success",
        summary: "Repull queued",
        detail:
          response.message ||
          `Started new scrape from job ${job.id} configuration`,
        life: 3000,
      });
      await loadScrapeJobs();
    } catch (error) {
      console.error("Error repulling scrape job:", error);
      toast.current?.show({
        severity: "error",
        summary: "Repull failed",
        detail: "Failed to repull scrape job",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReconcileStatuses = async () => {
    trackToolAction("DataHealthTool", "reconcile_status");
    try {
      setReconciling(true);
      const response = await apiService.scrapers.reconcileScrapeJobStatus();
      toast.current?.show({
        severity:
          response.updated > 0 || response.deleted > 0 ? "success" : "info",
        summary: "Status reconcile",
        detail:
          response.message ||
          `Updated ${response.updated}, deleted ${response.deleted}, of ${response.scanned} jobs`,
        life: 4000,
      });
      await loadScrapeJobs();
    } catch (error) {
      console.error("Error reconciling scrape statuses:", error);
      toast.current?.show({
        severity: "error",
        summary: "Reconcile failed",
        detail: "Failed to reconcile scrape job statuses",
        life: 3000,
      });
    } finally {
      setReconciling(false);
    }
  };

  const handleDeleteScrapeJob = (job: ScrapeJob) => {
    confirmDialog({
      message:
        "Delete this scrape job from the active list? This keeps an audit trail in Deletion History.",
      header: "Confirm Scrape Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          setDeletingJobId(job.id);
          const response = await apiService.scrapers.deleteScrapeJob(job.id);
          toast.current?.show({
            severity: "success",
            summary: "Deleted",
            detail:
              response.message || `Job ${job.id} deleted and audit trail updated`,
            life: 3000,
          });
          await Promise.all([loadScrapeJobs(), loadDeletionHistory()]);
        } catch (error) {
          console.error("Error deleting scrape job:", error);
          toast.current?.show({
            severity: "error",
            summary: "Delete failed",
            detail: "Failed to delete scrape job",
            life: 3000,
          });
        } finally {
          setDeletingJobId(null);
        }
      },
      reject: () => setDeletingJobId(null),
    });
  };

  const statusBodyTemplate = (rowData: ScrapeJob) => {
    const getSeverity = (status: string) => {
      switch (status) {
        case "completed":
          return "success";
        case "running":
          return "info";
        case "failed":
          return "danger";
        default:
          return "secondary";
      }
    };

    return (
      <span className={`p-tag p-tag-${getSeverity(rowData.status)}`}>
        {rowData.status.toUpperCase()}
      </span>
    );
  };

  const actionBodyTemplate = (rowData: ScrapeJob) => {
    return (
      <div className="flex gap-2">
        {rowData.status === "completed" && (
          <Button
            icon="pi pi-download"
            className="p-button-sm p-button-success"
            tooltip="Build custom CSV export"
            onClick={() => {
              trackToolAction("DataHealthTool", "custom_export_open");
              setExportJob(rowData);
            }}
          />
        )}
        {rowData.status !== "running" && (
          <Button
            icon="pi pi-refresh"
            className="p-button-sm p-button-warning"
            tooltip="Repull this scrape config"
            onClick={() => handleRepull(rowData)}
          />
        )}
        <Button
          icon="pi pi-trash"
          className="p-button-sm p-button-danger"
          tooltip="Delete this scrape job"
          onClick={() => handleDeleteScrapeJob(rowData)}
          disabled={
            rowData.status === "running" ||
            rowData.status === "queued" ||
            deletingJobId === rowData.id
          }
        />
        {rowData.bigQueryTableName && rowData.bigQueryTableName.length > 0 && (
          <Button
            icon="pi pi-database"
            className="p-button-sm p-button-info"
            tooltip={`BigQuery Tables: ${rowData.bigQueryTableName.join(", ")}`}
            onClick={() => {
              navigator.clipboard.writeText(
                rowData.bigQueryTableName!.join(", ")
              );
              toast.current?.show({
                severity: "info",
                summary: "Copied",
                detail: "BigQuery table names copied to clipboard",
                life: 2000,
              });
            }}
          />
        )}
      </div>
    );
  };

  const historyTabs: MenuItem[] = [
    { label: "Scrape History", icon: "pi pi-list" },
    { label: "Deletion History", icon: "pi pi-history" },
  ];

  return (
    <div>
      <Toast ref={toast}></Toast>
      <ConfirmDialog />
      <CustomAssetExportDialog
        jobId={exportJob?.id ?? null}
        jobTitle={exportJob?.title}
        visible={!!exportJob}
        onHide={() => setExportJob(null)}
        onSuccess={(message) =>
          toast.current?.show({
            severity: "success",
            summary: "Download",
            detail: message,
            life: 3000,
          })
        }
        onError={(message) =>
          toast.current?.show({
            severity: "error",
            summary: "Export failed",
            detail: message,
            life: 3000,
          })
        }
      />

      {/* Scrape Configuration */}
      <div className="grid">
        <div className="col-12 md:col-6">
          <Panel header="Scrape Configuration">
            <div className="p-fluid">
              <div className="field">
                <label htmlFor="title">Title *</label>
                <InputText
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Q4 Music Assets Scrape"
                />
              </div>
              <div className="field">
                <label htmlFor="brand">Brand *</label>
                <InputText
                  id="brand"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="e.g., Universal Music"
                />
              </div>
              <div className="field">
                <label htmlFor="tableName">Table Name *</label>
                <InputText
                  id="tableName"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value.toUpperCase())}
                  placeholder="e.g., MUSIC"
                  maxLength={6}
                  className={
                    !isValidTableName(tableName) && tableName ? "p-invalid" : ""
                  }
                />
                <small className="text-gray-600">
                  1-6 uppercase letters only (e.g., MUSIC, ASSETS)
                </small>
              </div>
            </div>
          </Panel>
        </div>

        <div className="col-12 md:col-6">
          <Panel header="Asset IDs Input">
            <AssetIdInput
              value={pasteText}
              onChange={setPasteText}
              onAssetIdsParsed={handleAssetIdsParsed}
              placeholder="Paste asset IDs here or drag & drop CSV file"
              rows={4}
            />
          </Panel>
        </div>

        {/* Asset IDs Preview */}
        {(assetIds.length > 0 || invalidIds.length > 0) && (
          <div className="col-12">
            <Panel header="Asset IDs Preview" toggleable>
              {assetIds.length > 0 && (
                <div>
                  <h5>Valid Asset IDs ({assetIds.length})</h5>
                  <DataTable
                    value={assetIds.map((id) => ({ id }))}
                    paginator
                    rows={10}
                    dataKey="id"
                    responsiveLayout="scroll"
                  >
                    <Column field="id" header="Asset ID"></Column>
                  </DataTable>
                </div>
              )}
              {invalidIds.length > 0 && (
                <div className="mt-4">
                  <h5>Invalid IDs ({invalidIds.length})</h5>
                  <DataTable
                    value={invalidIds.map((id) => ({ id }))}
                    paginator
                    rows={5}
                  >
                    <Column field="id" header="Invalid ID"></Column>
                  </DataTable>
                </div>
              )}
            </Panel>
          </div>
        )}

        {/* Start Scrape Button */}
        {assetIds.length > 0 &&
          title &&
          brand &&
          tableName &&
          isValidTableName(tableName) && (
            <div className="col-12">
              <div className="text-center">
                <Button
                  label={`Start Scraping ${assetIds.length} Assets`}
                  icon="pi pi-play"
                  onClick={handleStartScrape}
                  className="p-button-success p-button-lg"
                  loading={loading}
                  disabled={loading}
                />
              </div>
            </div>
          )}
      </div>

      {/* Historical Scrapes */}
      <div className="mt-4">
        <Panel header="Job History">
          <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <TabMenu
              model={historyTabs}
              activeIndex={activeHistoryTab}
              onTabChange={(e) => setActiveHistoryTab(e.index)}
            />
            {activeHistoryTab === 0 && (
              <Button
                label="Reconcile stuck statuses"
                icon="pi pi-sync"
                className="p-button-sm p-button-outlined"
                onClick={handleReconcileStatuses}
                loading={reconciling}
                disabled={reconciling || loadingJobs}
                tooltip="Mark queued/running jobs completed when BigQuery already has all original asset IDs (or all batches reported)"
              />
            )}
          </div>

          {activeHistoryTab === 0 ? (
            <DataTable
              value={scrapeJobs}
              paginator
              rows={10}
              dataKey="id"
              responsiveLayout="scroll"
              emptyMessage="No scrape jobs found"
              loading={loadingJobs}
            >
              <Column field="title" header="Title" sortable></Column>
              <Column field="brand" header="Brand" sortable></Column>
              <Column
                field="tableName"
                header="Table Name"
                body={(rowData: ScrapeJob) => rowData.tableName || "N/A"}
                sortable
              ></Column>
              <Column field="assetCount" header="Asset Count" sortable></Column>
              <Column
                field="status"
                header="Status"
                body={statusBodyTemplate}
                sortable
              ></Column>
              <Column field="createdBy" header="Created By" sortable></Column>
              <Column field="createdAt" header="Created At" sortable></Column>
              <Column field="completedAt" header="Completed At" sortable></Column>
              <Column
                field="bigQueryTableName"
                header="BigQuery Tables"
                body={(rowData: ScrapeJob) =>
                  rowData.bigQueryTableName &&
                  rowData.bigQueryTableName.length > 0
                    ? rowData.bigQueryTableName.join(", ")
                    : "N/A"
                }
                sortable
              ></Column>
              <Column header="Actions" body={actionBodyTemplate}></Column>
            </DataTable>
          ) : (
            <DataTable
              value={deletedScrapeJobs}
              paginator
              rows={10}
              dataKey="id"
              responsiveLayout="scroll"
              emptyMessage="No deleted scrape jobs found"
              loading={loadingJobs}
            >
              <Column field="title" header="Title" sortable></Column>
              <Column field="brand" header="Brand" sortable></Column>
              <Column field="tableName" header="Table Name" sortable></Column>
              <Column field="assetCount" header="Asset Count" sortable></Column>
              <Column field="createdBy" header="Created By" sortable></Column>
              <Column field="createdAt" header="Created At" sortable></Column>
              <Column field="deletedBy" header="Deleted By" sortable></Column>
              <Column field="deletedAt" header="Deleted At" sortable></Column>
            </DataTable>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default MusicAssetScraper;
